import type { SupabaseClient } from "@supabase/supabase-js";
import type { NestedCompany, NestedCurrency, NestedDeal, NestedTeam } from "@revelate/types";

/** Read-only projection of existing fields. No activity export or new CRM source. */
export async function loadForecastPresentationInputs(db: SupabaseClient, companyId: number) {
  const [companyResult, currencyResult, teamResult] = await Promise.all([
    db.from("companies").select("*").eq("id", companyId).single(),
    db.from("currencies").select("*,exchange_rates!exchange_rates_from_currency_id_fkey(*)").eq("company_id", companyId),
    db.from("teams").select("*,users(*)").eq("company_id", companyId),
  ]);
  if (companyResult.error || currencyResult.error || teamResult.error) throw new Error("Forecast history context is unavailable.");
  const deals: NestedDeal[] = [];
  for (let page = 0; page < 50; page++) {
    const result = await db.from("deals")
      .select("id,name,company_id,value,currency_id,deal_type,provider,provider_id,pipeline,closed_at,is_closed,is_won,is_lost,created_at,updated_at,provider_data,likelihood_to_close,next_step,next_step_updated_at,last_activity_at,users:user_deals(user_id,role,data:users(id,first_name,last_name))")
      .eq("company_id", companyId).eq("provider", "hubspot")
      .order("id", { ascending: true }).range(page * 1000, (page + 1) * 1000 - 1).abortSignal(AbortSignal.timeout(30_000));
    if (result.error) throw new Error("Forecast history could not be loaded.");
    deals.push(...result.data as unknown as NestedDeal[]);
    if (result.data.length < 1000) return {
      rawDeals: deals, company: companyResult.data as NestedCompany,
      currencies: currencyResult.data as NestedCurrency[], teams: teamResult.data as NestedTeam[],
    };
  }
  throw new Error("Forecast history exceeds the supported transport size.");
}
